import { Field, useField, useFormikContext } from 'formik';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, FC, useCallback } from 'react';

import { getWidgetBgDesktopImage, getWidgetBgMobileImage, useAppDispatch, useAppSelector } from '../../lib';
import { CheckboxField, ColorPickerField, FormRow, FormRowsDivider, ImagePickerField, InputField } from '../fields';
import { closeSidebar } from '../../store';
import { GameId } from '../../types';
import { MenuItem } from '@mui/material';
import { boxVariants } from '../../games/common/box-variants';

const getDefaultWheelCoupons = (amount: number) => {
  const winChance = (100 / amount).toFixed(2);

  return new Array(amount).fill({ winChance: +winChance, wixCouponId: '' });
};

const WidgetSettings = () => {
  const dispatch = useAppDispatch();
  const [field] = useField<GameId>('campaign.gameId');
  const { setFieldValue } = useFormikContext();
  const { t } = useTranslation();

  const closeSideBar = useCallback(() => {
    dispatch(closeSidebar());
  }, [dispatch]);

  return (
    <>
      {field.value === GameId.LUCKY && (
        <>
          <FormRow>
            <Field
              name="gameData.colors.primary"
              component={ColorPickerField}
              label={t('pages.campaign.rouletteSettings.texts.pointerColor')}
            />
          </FormRow>

          <FormRowsDivider />
        </>
      )}

      {field.value === GameId.WHEEL && (
        <>
          <FormRow>
            <Field
              name="gameData.slotsAmount"
              component={InputField}
              label={t('pages.campaign.sideBar.slotsAmount')}
              select
              defaultValue="8"
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setFieldValue('campaign.wixCoupons', getDefaultWheelCoupons(+event.target.value));
              }}
            >
              {[4, 6, 8, 10, 12].map((amount) => (
                <MenuItem key={amount} value={amount}>
                  {amount}
                </MenuItem>
              ))}
            </Field>
          </FormRow>

          <FormRowsDivider />

          <FormRow>
            <Field name="gameData.colors.primary.bg" component={ColorPickerField} label={t('pages.campaign.sideBar.evenSlots')} />
          </FormRow>

          <FormRow>
            <Field name="gameData.colors.primary.text" component={ColorPickerField} label={t('pages.campaign.sideBar.evenSlotsText')} />
          </FormRow>

          <FormRowsDivider />

          <FormRow>
            <Field
              name="gameData.colors.secondary.bg"
              component={ColorPickerField}
              label={t('pages.campaign.sideBar.oddSlots')} 
            />
          </FormRow>

          <FormRow>
            <Field name="gameData.colors.secondary.text" component={ColorPickerField} label={t('pages.campaign.sideBar.oddSlotsText')} />
          </FormRow>

          <FormRowsDivider />

          <FormRow>
            <Field name="gameData.colors.tertiary" component={ColorPickerField} label={t('pages.campaign.sideBar.pointerColor')} />
          </FormRow>

          <FormRowsDivider />
        </>
      )}

      <FormRow>
        <Field
          name="campaign.colors.bg"
          component={ColorPickerField}
          label={t('pages.campaign.rouletteSettings.texts.backgroundColor')}
        />
      </FormRow>

      <FormRowsDivider />

      <FormRow>
        <Field
          name="campaign.useBgImage"
          component={CheckboxField}
          label={t('pages.campaign.rouletteSettings.texts.useBackgroundImage')}
        />
      </FormRow>

      <FormRow>
        <Field
          name="campaign.bgImageUrl.desktop"
          component={ImagePickerField}
          label={t('pages.campaign.rouletteSettings.texts.desktopBackgroundImage')}
          handleWixModalOpened={closeSideBar}
          renderPreview={(value: string) => value && <Preivew src={getWidgetBgDesktopImage(value)} />}
          uploadUrl="/files-uploader/upload?type=widget_bg_desktop"
        />
      </FormRow>

      <FormRow>
        <Field
          name="campaign.bgImageUrl.mobile"
          component={ImagePickerField}
          label={t('pages.campaign.rouletteSettings.texts.mobileBackgroundImage')}
          handleWixModalOpened={closeSideBar}
          renderPreview={(value: string) => value && <Preivew src={getWidgetBgMobileImage(value)} />}
          uploadUrl="/files-uploader/upload?type=widget_bg_mobile"
        />
      </FormRow>

      {field.value === GameId.LUCKY && (
        <>
          <FormRowsDivider />

          <FormRow>
            <Field
              name="gameData.defaultImageSrc"
              label={t('pages.campaign.rouletteSettings.texts.defaultPrizeImage')}
              component={ImagePickerField}
              imageVariants={boxVariants}
              handleWixModalOpened={closeSideBar}
              uploadUrl="/files-uploader/upload?type=prize"
            />
          </FormRow>
        </>
      )}
    </>
  );
};

type ResultScreenSettingsProps = {
  sliceName: string;
};

const ResultScreenSettings: FC<ResultScreenSettingsProps> = ({ sliceName }) => {
  return (
    <>
      <FormRow>
        <Field name={`${sliceName}.bgColor`} component={ColorPickerField} label="Background color" />
      </FormRow>

      <FormRow>
        <Field name={`${sliceName}.closeButtonColor`} component={ColorPickerField} label="Close button color" />
      </FormRow>
    </>
  );
};

const DisabledScreenSettings = () => {
  return (
    <FormRow>
      <Field name="campaign.disabledScreen.bgColor" component={ColorPickerField} label="Background color" alpha />
    </FormRow>
  );
};

const widgetSettingsContent = {
  initial: <WidgetSettings />,
  'screen-win': <ResultScreenSettings sliceName="campaign.winScreen" />,
  'screen-loose': <ResultScreenSettings sliceName="campaign.looseScreen" />,
  'screen-disabled': <DisabledScreenSettings />,
};

export const WidgetSettingsSidebar = () => {
  const {
    editor: { step },
  } = useAppSelector((state) => state.app);

  return <WidgetSettingsWrapper>{widgetSettingsContent[step]}</WidgetSettingsWrapper>;
};

const WidgetSettingsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Preivew = styled.img`
  width: 200px;
  height: auto;
  margin-bottom: 8px;
`;
